import { useState } from "react";
import {
  ExternalLink,
  Link2,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import {
  LINK_CATEGORIES,
  PINNED_LINK_CAP,
  type LinkInput,
  type LinkPatch,
  type ResourceLink,
} from "@/hooks/useLinks";

// ---------------------------------------------------------------------------
// Category styling
// ---------------------------------------------------------------------------

const CATEGORY_TONE: Record<string, string> = {
  spec: "bg-brand-navy-100 dark:bg-brand-navy-500/15 text-brand-navy-700 dark:text-brand-navy-300 ring-brand-navy-200 dark:ring-brand-navy-500/30",
  spreadsheet: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30",
  folder: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-200 dark:ring-amber-500/30",
  design: "bg-fuchsia-100 dark:bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 ring-fuchsia-200 dark:ring-fuchsia-500/30",
  vendor: "bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 ring-orange-200 dark:ring-orange-500/30",
  dashboard: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300 ring-sky-200 dark:ring-sky-500/30",
  reference: "bg-muted text-ink-2 ring-line-strong",
  other: "bg-raised text-ink-3 ring-line",
};

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  LINK_CATEGORIES.map((c) => [c.value, c.label]),
);

function categoryLabel(c: string | null): string {
  if (!c) return "Link";
  return CATEGORY_LABEL[c] ?? c;
}

function categoryTone(c: string | null): string {
  if (!c) return CATEGORY_TONE.other;
  return CATEGORY_TONE[c] ?? CATEGORY_TONE.other;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  links: ResourceLink[] | undefined;
  isLoading: boolean;
  onAdd: (payload: LinkInput) => Promise<unknown> | void;
  onUpdate: (patch: LinkPatch & { id: number }) => Promise<unknown> | void;
  onDelete: (id: number) => Promise<unknown> | void;
  /** What kind of entity these links belong to. Used in copy. */
  entityLabel?: "project";
}

export function ResourcesPanel({
  links,
  isLoading,
  onAdd,
  onUpdate,
  onDelete,
  entityLabel = "project",
}: Props) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const list = links ?? [];
  const pinnedCount = list.filter((l) => l.pinned).length;

  const handleAdd = async (payload: LinkInput) => {
    if (payload.pinned && pinnedCount >= PINNED_LINK_CAP) {
      toast.error(`Only ${PINNED_LINK_CAP} links can be pinned. Unpin one first.`);
      return;
    }
    try {
      await onAdd(payload);
      toast.success("Link added");
      setShowAdd(false);
    } catch (err) {
      toast.error((err as Error).message || "Failed to add link");
    }
  };

  const handleUpdate = async (patch: LinkPatch & { id: number }) => {
    if (patch.pinned === true) {
      // user is pinning — count this link as one of the cap
      const currentlyPinned = list
        .filter((l) => l.pinned && l.id !== patch.id)
        .length;
      if (currentlyPinned >= PINNED_LINK_CAP) {
        toast.error(`Only ${PINNED_LINK_CAP} links can be pinned. Unpin one first.`);
        return;
      }
    }
    try {
      await onUpdate(patch);
      setEditingId(null);
    } catch (err) {
      toast.error((err as Error).message || "Failed to save");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await onDelete(id);
      setDeleteConfirm(null);
      toast.success("Link removed");
    } catch (err) {
      toast.error((err as Error).message || "Failed to delete");
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-brand-navy-500" />
            <h3 className="text-sm font-semibold text-ink">Resources</h3>
            <span className="text-[11px] text-ink-muted">
              {list.length} link{list.length === 1 ? "" : "s"}
              {pinnedCount > 0 && ` · ${pinnedCount} pinned`}
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-ink-muted">
            Links to spreadsheets, folders, dashboards, and other artifacts that live with this {entityLabel}.
            Pin up to {PINNED_LINK_CAP} to surface them on the Overview.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Add link
        </button>
      </div>

      {showAdd && (
        <LinkForm
          onCancel={() => setShowAdd(false)}
          onSubmit={handleAdd}
          submitLabel="Add link"
          canPin={pinnedCount < PINNED_LINK_CAP}
        />
      )}

      {/* List */}
      {isLoading ? (
        <div className="rounded-xl border border-line bg-surface p-8 text-center text-sm text-ink-muted">
          Loading…
        </div>
      ) : list.length === 0 && !showAdd ? (
        <div className="rounded-xl border border-dashed border-line-strong bg-surface p-10 text-center">
          <Link2 className="mx-auto h-6 w-6 text-ink-faint" />
          <div className="mt-2 text-sm font-medium text-ink-2">No links yet</div>
          <p className="mt-1 text-xs text-ink-muted">
            Add a SharePoint folder, spreadsheet, dashboard, or any other artifact tied to this {entityLabel}.
          </p>
          <button
            onClick={() => setShowAdd(true)}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Add the first link
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((link) =>
            editingId === link.id ? (
              <li key={link.id}>
                <LinkForm
                  initial={link}
                  onCancel={() => setEditingId(null)}
                  onSubmit={(patch) =>
                    handleUpdate({ id: link.id, ...patch })
                  }
                  submitLabel="Save"
                  canPin={link.pinned || pinnedCount < PINNED_LINK_CAP}
                />
              </li>
            ) : (
              <li key={link.id}>
                <LinkRow
                  link={link}
                  pinDisabled={!link.pinned && pinnedCount >= PINNED_LINK_CAP}
                  onTogglePin={() =>
                    handleUpdate({ id: link.id, pinned: !link.pinned })
                  }
                  onEdit={() => setEditingId(link.id)}
                  onAskDelete={() => setDeleteConfirm(link.id)}
                  confirmingDelete={deleteConfirm === link.id}
                  onCancelDelete={() => setDeleteConfirm(null)}
                  onConfirmDelete={() => handleDelete(link.id)}
                />
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface RowProps {
  link: ResourceLink;
  pinDisabled: boolean;
  onTogglePin: () => void;
  onEdit: () => void;
  onAskDelete: () => void;
  confirmingDelete: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}

function LinkRow({
  link,
  pinDisabled,
  onTogglePin,
  onEdit,
  onAskDelete,
  confirmingDelete,
  onCancelDelete,
  onConfirmDelete,
}: RowProps) {
  const safeUrl = link.url.startsWith("http") ? link.url : `https://${link.url}`;
  return (
    <div
      className={cn(
        "group rounded-xl border bg-surface p-3 transition-colors",
        link.pinned ? "border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50/30 dark:bg-brand-navy-500/10" : "border-line",
      )}
    >
      <div className="flex items-start gap-3">
        <a
          href={safeUrl}
          target="_blank"
          rel="noreferrer"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-raised text-ink-muted hover:bg-brand-navy-100 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-600 dark:hover:text-brand-navy-400 transition-colors"
          title="Open in new tab"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={safeUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-semibold text-ink hover:text-brand-navy-700 dark:hover:text-brand-navy-300 hover:underline truncate"
            >
              {link.label}
            </a>
            <span
              className={cn(
                "rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1",
                categoryTone(link.category),
              )}
            >
              {categoryLabel(link.category)}
            </span>
            {link.pinned && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-brand-navy-100 dark:bg-brand-navy-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-navy-700 dark:text-brand-navy-300 ring-1 ring-brand-navy-200 dark:ring-brand-navy-500/30">
                <Pin className="h-2.5 w-2.5" /> Pinned
              </span>
            )}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-ink-muted">
            {safeUrl}
          </div>
          {link.notes && (
            <div className="mt-1.5 text-xs text-ink-3 whitespace-pre-wrap">
              {link.notes}
            </div>
          )}
          <div className="mt-1 text-[10px] text-ink-subtle">
            {link.added_by ? `Added by ${link.added_by} · ` : "Added "}
            {formatDate(link.created_at)}
          </div>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          {confirmingDelete ? (
            <>
              <button
                onClick={onConfirmDelete}
                className="rounded-md bg-red-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-red-700"
              >
                Delete?
              </button>
              <button
                onClick={onCancelDelete}
                className="rounded-md p-1 text-ink-subtle hover:bg-raised hover:text-ink-3"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onTogglePin}
                disabled={pinDisabled}
                title={
                  link.pinned
                    ? "Unpin from Overview"
                    : pinDisabled
                      ? `Only ${PINNED_LINK_CAP} pins allowed`
                      : "Pin to Overview"
                }
                className="rounded-md p-1.5 text-ink-subtle hover:bg-raised hover:text-brand-navy-600 dark:hover:text-brand-navy-400 disabled:cursor-not-allowed disabled:opacity-30"
              >
                {link.pinned ? (
                  <PinOff className="h-3.5 w-3.5" />
                ) : (
                  <Pin className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                onClick={onEdit}
                title="Edit"
                className="rounded-md p-1.5 text-ink-subtle hover:bg-raised hover:text-ink-2"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={onAskDelete}
                title="Delete"
                className="rounded-md p-1.5 text-ink-subtle hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form (add + edit)
// ---------------------------------------------------------------------------

interface FormProps {
  initial?: ResourceLink;
  onCancel: () => void;
  onSubmit: (payload: LinkInput) => void;
  submitLabel: string;
  canPin: boolean;
}

function LinkForm({ initial, onCancel, onSubmit, submitLabel, canPin }: FormProps) {
  const [label, setLabel] = useState(initial?.label ?? "");
  const [url, setUrl] = useState(initial?.url ?? "");
  const [category, setCategory] = useState<string>(initial?.category ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [pinned, setPinned] = useState(initial?.pinned ?? false);
  const [addedBy, setAddedBy] = useState(initial?.added_by ?? "");

  const canSubmit = label.trim().length > 0 && url.trim().length > 0;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      label: label.trim(),
      url: url.trim(),
      category: category || null,
      notes: notes.trim() || null,
      pinned,
      added_by: addedBy.trim() || null,
    });
  };

  return (
    <div className="rounded-xl border border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50/30 dark:bg-brand-navy-500/10 p-4 space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Label" required>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Customer Master Cleanup Sheet"
            className={inputCls}
            autoFocus
          />
        </Field>
        <Field label="Category">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={inputCls}
          >
            <option value="">— uncategorized —</option>
            {LINK_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="URL" required>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
          className={inputCls}
        />
      </Field>
      <Field label="Notes" hint="What is this and why does it matter?">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional context — e.g. owner, refresh cadence, what to look for"
          rows={2}
          className={cn(inputCls, "resize-y")}
        />
      </Field>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Added by" hint="Optional">
          <input
            type="text"
            value={addedBy}
            onChange={(e) => setAddedBy(e.target.value)}
            placeholder="Your name"
            className={inputCls}
          />
        </Field>
        <label className="flex items-center gap-2 self-end pb-1.5 text-xs text-ink-2">
          <input
            type="checkbox"
            checked={pinned}
            onChange={(e) => setPinned(e.target.checked)}
            disabled={!canPin && !pinned}
            className="h-3.5 w-3.5 rounded border-line-strong text-brand-navy-600 dark:text-brand-navy-400 focus:ring-brand-navy-500 disabled:opacity-40"
          />
          <Pin className="h-3 w-3 text-brand-navy-500" />
          <span>Pin to Overview {canPin || pinned ? "" : `(${PINNED_LINK_CAP}-pin limit reached)`}</span>
        </label>
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-40"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-navy-400 focus:outline-none focus:ring-2 focus:ring-brand-navy-100";

const labelCls =
  "block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1";

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className={labelCls}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
        {hint && <span className="ml-2 normal-case text-ink-subtle font-normal">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Pinned chip strip — used on Overview headers
// ---------------------------------------------------------------------------

export function PinnedLinksStrip({
  links,
  onSeeAll,
}: {
  links: ResourceLink[] | undefined;
  onSeeAll: () => void;
}) {
  const all = links ?? [];
  const pinned = all.filter((l) => l.pinned).slice(0, PINNED_LINK_CAP);
  if (pinned.length === 0) return null;
  const more = all.length - pinned.length;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {pinned.map((link) => {
        const safeUrl = link.url.startsWith("http") ? link.url : `https://${link.url}`;
        return (
          <a
            key={link.id}
            href={safeUrl}
            target="_blank"
            rel="noreferrer"
            title={link.notes ?? safeUrl}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 transition-colors hover:bg-surface hover:shadow-sm",
              categoryTone(link.category),
            )}
          >
            <ExternalLink className="h-3 w-3" />
            <span className="max-w-[180px] truncate">{link.label}</span>
          </a>
        );
      })}
      {more > 0 && (
        <button
          onClick={onSeeAll}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-line-strong bg-surface px-2.5 py-1 text-[11px] font-medium text-ink-muted hover:border-line-strong hover:text-ink-2"
        >
          +{more} more
        </button>
      )}
    </div>
  );
}
